import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { cn } from "../lib/cn.js";

/**
 * A row's actions, behind one control.
 *
 * ==========================================================================================
 * WHY THIS EXISTS RATHER THAN FOUR BUTTONS IN A CELL
 * ==========================================================================================
 *
 * A table row that ends in four buttons has its actions competing with its data for the reader's
 * attention, and the column has to be wide enough for the longest label in the set. One trigger keeps
 * the row scannable and lets the labels be as long as they need to be — which matters here, where
 * "It stopped" and "Remove the record" mean different things and cannot be shortened into icons.
 *
 * Built here rather than taken from HeroUI: it is declared as an optional peer dependency of the
 * domain packages but is not installed and nothing imports it. Adding a component library for one
 * menu would be a larger decision than this control, and every other control in this package —
 * `Dialog`, `Table`, `StatCard` — is hand-built for the same reason.
 *
 * ==========================================================================================
 * IT IS PORTALLED AND FIXED-POSITIONED, AND THAT IS NOT OVER-ENGINEERING
 * ==========================================================================================
 *
 * The first version was `absolute` inside the trigger's cell, and the menu on the LAST ROW opened
 * off the bottom of the table — you had to scroll to reach what you had just clicked.
 *
 * Two separate causes, which is why neither half of the fix is optional:
 *
 *   THE TABLE CLIPS IT. `Table`'s wrapper is `overflow-x-auto` so a wide table scrolls itself rather
 *   than widening the page. `overflow-x: auto` with a default `overflow-y: visible` COMPUTES to
 *   `overflow-y: auto`, which makes the wrapper a scroll container — and an absolutely positioned
 *   descendant is clipped by it however it is aligned. Flipping the menu upward would not have
 *   escaped that; only leaving the subtree does.
 *
 *   THERE MAY BE NO ROOM BELOW. Even unclipped, a menu that always opens downward runs off the
 *   viewport on the last row of a long table.
 *
 * So it renders into `document.body` with `position: fixed`, measured from the trigger, and flips
 * above the trigger when the space below cannot hold it.
 *
 * ==========================================================================================
 * WHAT IT DOES ABOUT KEYBOARDS AND SCREEN READERS
 * ==========================================================================================
 *
 * The trigger is a real `button` with `aria-haspopup="menu"` and `aria-expanded`. The list is a
 * `menu` of `menuitem`s. Escape closes and returns focus to the trigger — without that a keyboard
 * user is stranded in a closed menu's former position. A pointer press outside closes it, and so does
 * scrolling, because a fixed-position menu measured once would otherwise drift away from its row.
 *
 * It deliberately does NOT trap focus or implement arrow-key roving. A menu of three items reached by
 * Tab behaves correctly for keyboard and screen-reader users; roving focus is what a menubar needs,
 * and adding it here would be inventing behaviour nothing in this application uses.
 */
export interface ActionMenuItem {
  label: string;
  onSelect: () => void;
  /** For the one action in a set that removes something. Rendered in the danger colour. */
  destructive?: boolean;
  disabled?: boolean;
  /**
   * Makes this a TOGGLE rather than an action: `menuitemcheckbox`, with a tick when it is on.
   *
   * ==========================================================================================
   * THE ROLE IS THE POINT, NOT THE TICK
   * ==========================================================================================
   *
   * A `menuitem` is a verb — pressing it does something and the menu closes. An item that carries
   * STATE is a different thing, and announcing one as the other tells a screen-reader user that
   * "Show archived" is an action they have not taken, whether or not the archive is already on
   * screen. `menuitemcheckbox` with `aria-checked` is the ARIA answer, and it is what makes the tick
   * a redundant second signal rather than the only one.
   *
   * Omit it entirely for an ordinary action. `false` is a real value meaning "a toggle, currently
   * off"; `undefined` means "not a toggle at all", and the two must not collapse — an action rendered
   * with `aria-checked="false"` claims a state it does not have.
   */
  checked?: boolean;
}

export interface ActionMenuProps {
  items: ActionMenuItem[];
  /** Names the menu for a screen reader — "Actions for Damien — salary", not just "Actions". */
  label: string;
  /**
   * What the trigger shows instead of the three dots.
   *
   * FOR A TRIGGER THAT IS A THING RATHER THAN AN AFFORDANCE. The dots say "there are actions here",
   * which is right on a table row where the row is the subject. The account menu's trigger is the
   * member's own avatar: it is not an affordance beside a subject, it IS the subject, and replacing it
   * with dots would lose the one place a member sees themselves in the chrome.
   *
   * Same shape as `StatCard`'s `controls` and `Page.Title`'s `actions` — a `ReactNode`, so the primitive
   * does not learn what an avatar is.
   */
  trigger?: ReactNode;
  /**
   * Replaces the trigger button's own classes, for a trigger whose shape is not a 32px square — an
   * avatar wants `rounded-full` and its own size. The focus ring is re-declared by the caller rather
   * than merged, because merging two class strings that both set a radius is how you get whichever
   * Tailwind emitted last.
   */
  triggerClassName?: string;
}

/** Enough for the longest label this control is given, and the width the menu is pinned to. */
const MENU_WIDTH = 208;
const GAP = 4;

interface Placement {
  top: number;
  left: number;
  /** Above the trigger, when below would run off the viewport. */
  flipped: boolean;
}

export function ActionMenu({
  items,
  label,
  trigger,
  triggerClassName,
}: ActionMenuProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    setPlacement(null);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  /**
   * Where the menu goes, from the trigger's current position.
   *
   * The height is taken from the RENDERED menu rather than estimated from the item count, because an
   * item that wraps to two lines would make an estimate wrong in exactly the case — a long label near
   * the bottom of the screen — where the flip matters most.
   */
  const measure = useCallback(() => {
    const trigger = triggerRef.current?.getBoundingClientRect();
    if (!trigger) return;

    const height = menuRef.current?.offsetHeight ?? 0;
    const below = window.innerHeight - trigger.bottom;
    // Flip only when below genuinely cannot hold it AND above has more room. On a short viewport
    // where neither fits, flipping would push it off the top instead — the same bug, upside down.
    const flipped = height > 0 && below < height + GAP && trigger.top > below;

    setPlacement({
      top: flipped ? trigger.top - height - GAP : trigger.bottom + GAP,
      // Right-aligned to the trigger, then clamped so a menu near the left edge stays on screen.
      left: Math.max(GAP, trigger.right - MENU_WIDTH),
      flipped,
    });
  }, []);

  // AFTER layout and BEFORE paint, so the menu is never briefly drawn at 0,0 and then jumps.
  useLayoutEffect(() => {
    if (open) measure();
  }, [open, items.length, measure]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) === true) return;
      if (menuRef.current?.contains(target) === true) return;
      close(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") close(true);
    }
    /*
     * REPOSITION ON SCROLL, DO NOT CLOSE.
     *
     * Closing was the first attempt and it is wrong twice over. A fixed menu that ignores scrolling
     * slides away from its row, so something has to happen — but closing means any scroll dismisses
     * a menu the member just opened, including the scroll a browser performs to bring the thing they
     * are reaching for into view. It made the last row's menu unusable for the second time, by a
     * different mechanism than the one being fixed.
     *
     * Capture phase, because the scroll may happen in the page's own scrolling element rather than on
     * `window`, and a bubbling listener would never see it.
     */
    function onScrollOrResize(): void {
      measure();
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, close, measure]);

  const menu = (
    <div
      ref={menuRef}
      id={menuId}
      role="menu"
      aria-label={label}
      style={{
        position: "fixed",
        width: MENU_WIDTH,
        top: placement?.top ?? 0,
        left: placement?.left ?? 0,
        // Hidden until measured, so it is never briefly drawn in the wrong place.
        visibility: placement ? "visible" : "hidden",
      }}
      className={cn(
        "z-50 overflow-hidden rounded-control border border-input bg-page py-1 shadow-lg",
      )}
    >
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          // A toggle and an action are different things to assistive technology — see `checked`.
          role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
          {...(item.checked === undefined ? {} : { "aria-checked": item.checked })}
          disabled={item.disabled ?? false}
          onClick={() => {
            close(false);
            item.onSelect();
          }}
          className={cn(
            "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm",
            "transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
            item.destructive === true ? "text-danger" : "text-ink",
          )}
        >
          {/*
            THE TICK'S ROOM IS RESERVED WHETHER OR NOT IT IS DRAWN, so a menu of toggles does not
            shuffle its labels sideways as they are switched on and off. `aria-hidden` because
            `aria-checked` above already says this, and in words rather than in a glyph.

            A menu with no toggles in it gets no gutter at all: an action-only menu should look
            exactly as it did before this prop existed.
          */}
          {item.checked === undefined ? null : (
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className={cn("size-3.5 shrink-0", item.checked ? "opacity-100" : "opacity-0")}
            >
              <path d="M3 8.5l3.5 3.5L13 5" />
            </svg>
          )}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={() => (open ? close(false) : setOpen(true))}
        className={
          triggerClassName ??
          cn(
            "inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-control",
            "text-muted transition-colors outline-none hover:bg-accent hover:text-ink",
            "focus-visible:ring-[3px] focus-visible:ring-ring/50",
          )
        }
      >
        {trigger ?? (
          <>
            {/* Three dots, drawn rather than imported: an icon dependency for three circles is not a
                trade worth making, and this way the glyph cannot drift from the button's size. */}
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-4 w-4">
              <circle cx="10" cy="4" r="1.6" />
              <circle cx="10" cy="10" r="1.6" />
              <circle cx="10" cy="16" r="1.6" />
            </svg>
          </>
        )}
      </button>

      {/*
       * Portalled to the body so no ancestor's `overflow` can clip it. `typeof document` guards the
       * case where this renders without a DOM — the package is consumed by an SPA today, but a
       * component that throws during a server render is a trap laid for whoever adds one.
       */}
      {open && typeof document !== "undefined" ? createPortal(menu, document.body) : null}
    </>
  );
}
